/** Табель и загрузка (ТХ-05), топливо против нормы (ТХ-07), стоимость машино-часа (ТХ-06), выработка (КП-01) */
import {
  type CostReport,
  type FleetTimesheet,
  FUEL_SOURCES,
  type FuelFillRow,
  type FuelReport,
  IDLE_REASONS,
  type OutputReport,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../lib/api';
import { todayLocal } from '../../lib/format';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Input, Select } from '../../ui/form';
import { Panel } from '../../ui/layout';
import { LField } from '../schedule/LField';
import { dd, money, n1, num, useFleetRights, useMoney, useUnits } from './shared';

const monthStart = () => `${todayLocal().slice(0, 7)}-01`;

export function usePeriod() {
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(todayLocal());
  const ui = (
    <div className="mb-4 flex flex-wrap items-end gap-2">
      <LField label="С">
        <Input type="date" className="h-11" value={from} onChange={(e) => setFrom(e.target.value)} />
      </LField>
      <LField label="По">
        <Input type="date" className="h-11" value={to} onChange={(e) => setTo(e.target.value)} />
      </LField>
    </div>
  );
  return { from, to, ui };
}

const pct = (x: number | null) => (x == null ? '—' : `${x > 0 ? '+' : ''}${n1(x)} %`);

export function TimesheetPanel({ projectId }: { projectId: string | undefined }) {
  const p = usePeriod();
  const q = useQuery({
    queryKey: ['fleet', 'timesheet', projectId ?? '', p.from, p.to],
    queryFn: () => api<FleetTimesheet>('/fleet/timesheet', { query: { projectId, from: p.from, to: p.to } }),
  });
  return (
    <Panel title="Табель техники и загрузка" meta="часы по путевым листам">
      {p.ui}
      {q.isPending ? <Spinner /> : null}
      {q.isError ? <ErrorBox error={q.error} /> : null}
      {q.data && !q.data.rows.length ? <EmptyState title="Техники нет" /> : null}
      <ul className="divide-y divide-line">
        {(q.data?.rows ?? []).map((r) => (
          <li key={r.machineId} className="py-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-medium [overflow-wrap:anywhere]">
                {r.name}
                {r.regNumber ? ` · ${r.regNumber}` : ''}
              </span>
              <Badge tone={r.loadPct >= 70 ? 'success' : r.loadPct >= 30 ? 'warning' : 'neutral'}>
                загрузка {n1(r.loadPct)} %
              </Badge>
            </div>
            <p className="text-[12px] text-ink-2">
              работа {n1(r.workHours)} ч · простой {n1(r.idleHours)} ч · ремонт {n1(r.repairHours)} ч
              {Object.entries(r.idleByReason).length
                ? ` (${Object.entries(r.idleByReason)
                    .map(([k, v]) => `${IDLE_REASONS[k as keyof typeof IDLE_REASONS] ?? k}: ${n1(v)}`)
                    .join(', ')})`
                : ''}
            </p>
          </li>
        ))}
      </ul>
      {q.data?.fleetLoad.length ? (
        <p className="mt-3 text-[12px] text-ink-2">
          Загрузка парка по дням:{' '}
          {q.data.fleetLoad
            .filter((d) => d.pct > 0)
            .map((d) => `${dd(d.day).slice(0, 5)} — ${n1(d.pct)} %`)
            .join(' · ') || 'нет данных'}
        </p>
      ) : null}
    </Panel>
  );
}

export function FuelPanel({ projectId }: { projectId: string | undefined }) {
  const p = usePeriod();
  const rights = useFleetRights();
  const qc = useQueryClient();
  const [add, setAdd] = useState(false);
  const rep = useQuery({
    queryKey: ['fleet', 'fuel', projectId ?? '', p.from, p.to],
    queryFn: () => api<FuelReport>('/fleet/fuel/report', { query: { projectId, from: p.from, to: p.to } }),
  });
  const fills = useQuery({
    queryKey: ['fleet', 'fills', projectId ?? '', p.from, p.to],
    queryFn: () => api<FuelFillRow[]>('/fleet/fuel/fills', { query: { projectId, from: p.from, to: p.to } }),
  });
  return (
    <div className="flex flex-col gap-4">
      <Panel
        title="Расход топлива против нормы"
        actions={
          rights.canWaybill(projectId) ? (
            <Button variant="primary" onClick={() => setAdd(true)}>
              Записать заправку
            </Button>
          ) : null
        }
      >
        {p.ui}
        {rep.isPending ? <Spinner /> : null}
        {rep.isError ? <ErrorBox error={rep.error} /> : null}
        {rep.data && !rep.data.rows.length ? <EmptyState title="За период ни часов, ни заправок" /> : null}
        <ul className="divide-y divide-line">
          {(rep.data?.rows ?? []).map((r) => (
            <li key={r.machineId} className="py-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-medium [overflow-wrap:anywhere]">
                  {r.name}
                  {r.regNumber ? ` · ${r.regNumber}` : ''}
                </span>
                {r.deviationPct != null ? (
                  <Badge tone={r.over ? 'danger' : 'success'}>
                    {pct(r.deviationPct)} к норме{r.over ? ' — сверх допуска' : ''}
                  </Badge>
                ) : (
                  <Badge>нет нормы</Badge>
                )}
              </div>
              <p className="text-[12px] text-ink-2">
                {n1(r.workHours)} ч работы, {n1(r.idleHours)} ч простоя · по норме {n1(r.normLitres)} л · факт{' '}
                {n1(r.factLitres)} л ({r.factBasis === 'waybills' ? 'по путевым листам' : 'по заправкам'}) ·
                заправлено {n1(r.filledLitres)} л{r.amount != null ? ` · ${money(r.amount)}` : ''}
              </p>
            </li>
          ))}
        </ul>
      </Panel>
      <Panel title="Заправки" meta={`${fills.data?.length ?? 0}`}>
        <ul className="divide-y divide-line text-[14px]">
          {(fills.data ?? []).map((f) => (
            <li key={f.id} className="flex flex-wrap justify-between gap-2 py-1.5">
              <span>
                {dd(f.day)} · {f.machineName} · {n1(f.litres)} л
                <span className="block text-[12px] text-ink-2">
                  {FUEL_SOURCES[f.source]}
                  {f.receiptNo ? ` · чек ${f.receiptNo}` : ''}
                  {f.cardNo ? ` · карта ${f.cardNo}` : ''}
                </span>
              </span>
              {f.amount != null ? <span className="text-ink-2">{money(f.amount)}</span> : null}
            </li>
          ))}
        </ul>
      </Panel>
      {add && projectId ? (
        <FillDialog
          projectId={projectId}
          onClose={() => setAdd(false)}
          onSaved={() => {
            setAdd(false);
            void qc.invalidateQueries({ queryKey: ['fleet'] });
          }}
        />
      ) : null}
    </div>
  );
}

function FillDialog({
  projectId,
  onClose,
  onSaved,
}: {
  projectId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const units = useUnits(projectId);
  const money$ = useMoney();
  const [f, setF] = useState({
    machineId: '',
    day: todayLocal(),
    litres: '',
    price: '',
    source: 'station',
    receiptNo: '',
    cardNo: '',
  });
  const set = (k: keyof typeof f, v: string) => setF((x) => ({ ...x, [k]: v }));
  const save = useMutation({
    mutationFn: () =>
      api('/fleet/fuel/fills', {
        method: 'POST',
        body: {
          machineId: f.machineId,
          day: f.day,
          litres: num(f.litres),
          pricePerLitre: money$ ? num(f.price) : null,
          source: f.source,
          receiptNo: f.receiptNo.trim() || null,
          cardNo: f.cardNo.trim() || null,
        },
      }),
    onSuccess: () => {
      toast('Заправка записана');
      onSaved();
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title="Заправка"
      width="sm"
      footer={
        <>
          <Button onClick={onClose}>Отмена</Button>
          <Button
            variant="primary"
            disabled={!f.machineId || !num(f.litres)}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            Записать
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {save.isError ? <ErrorBox error={save.error} /> : null}
        <LField label="Машина">
          <Select className="h-11" value={f.machineId} onChange={(e) => set('machineId', e.target.value)}>
            <option value="">Выберите машину</option>
            {(units.data ?? []).map((u) => (
              <option key={u.machineId} value={u.machineId}>
                {u.name}
                {u.regNumber ? ` · ${u.regNumber}` : ''}
              </option>
            ))}
          </Select>
        </LField>
        <LField label="Дата">
          <Input type="date" className="h-11" value={f.day} onChange={(e) => set('day', e.target.value)} />
        </LField>
        <LField label="Литров">
          <Input
            inputMode="decimal"
            className="h-11"
            value={f.litres}
            onChange={(e) => set('litres', e.target.value)}
          />
        </LField>
        {money$ ? (
          <LField label="Цена литра, ₸">
            <Input
              inputMode="decimal"
              className="h-11"
              value={f.price}
              onChange={(e) => set('price', e.target.value)}
            />
          </LField>
        ) : null}
        <LField label="Откуда">
          <Select className="h-11" value={f.source} onChange={(e) => set('source', e.target.value)}>
            {Object.entries(FUEL_SOURCES).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </Select>
        </LField>
        <LField label="Номер чека">
          <Input className="h-11" value={f.receiptNo} onChange={(e) => set('receiptNo', e.target.value)} />
        </LField>
        <LField label="Топливная карта">
          <Input className="h-11" value={f.cardNo} onChange={(e) => set('cardNo', e.target.value)} />
        </LField>
      </div>
    </Dialog>
  );
}

export function CostPanel({ projectId }: { projectId: string | undefined }) {
  const p = usePeriod();
  const q = useQuery({
    queryKey: ['fleet', 'cost', projectId ?? '', p.from, p.to],
    queryFn: () => api<CostReport>('/fleet/cost/report', { query: { projectId, from: p.from, to: p.to } }),
  });
  return (
    <Panel title="Стоимость машино-часа и списание" meta="часы подписанных листов × ставка часа">
      {p.ui}
      {q.isPending ? <Spinner /> : null}
      {q.isError ? <ErrorBox error={q.error} /> : null}
      {q.data && !q.data.rows.length ? <EmptyState title="За период списаний нет" /> : null}
      {q.data && q.data.total == null && q.data.rows.length ? (
        <p className="mb-2 text-[13px] text-ink-2">Суммы видны с уровнем доступа к деньгам «Акты».</p>
      ) : null}
      <ul className="divide-y divide-line">
        {(q.data?.rows ?? []).map((r) => (
          <li key={r.machineId} className="py-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-medium [overflow-wrap:anywhere]">
                {r.name}
                {r.regNumber ? ` · ${r.regNumber}` : ''}
              </span>
              {r.amount != null ? <span className="font-medium">{money(r.amount)}</span> : null}
            </div>
            <p className="text-[12px] text-ink-2">
              {n1(r.hours)} ч{r.avgRate != null ? ` · ${money(r.avgRate)}/ч` : ''}
              {r.planRate != null ? ` при плане ${money(r.planRate)}/ч` : ''}
              {r.deviationPct != null ? ` (${pct(r.deviationPct)})` : ''}
              {r.fuelAmount ? ` · топливо ${money(r.fuelAmount)}` : ''}
              {r.serviceAmount ? ` · ТО ${money(r.serviceAmount)}` : ''}
            </p>
          </li>
        ))}
      </ul>
      {q.data?.total != null ? (
        <p className="mt-3 text-[14px] font-semibold">Итого списано: {money(q.data.total)}</p>
      ) : null}
    </Panel>
  );
}

export function OutputPanel({ projectId }: { projectId: string }) {
  const p = usePeriod();
  const q = useQuery({
    queryKey: ['fleet', 'output', projectId, p.from, p.to],
    queryFn: () => api<OutputReport>('/kpi/output', { query: { projectId, from: p.from, to: p.to } }),
  });
  return (
    <Panel title="Выработка по работам" meta="объём факта на машино-час против нормы компании">
      {p.ui}
      {q.isPending ? <Spinner /> : null}
      {q.isError ? <ErrorBox error={q.error} /> : null}
      {q.data && !q.data.rows.length ? (
        <EmptyState title="Работ с часами техники за период нет">
          Часы попадают сюда из подписанных путевых листов с указанной работой графика.
        </EmptyState>
      ) : null}
      <ul className="divide-y divide-line">
        {(q.data?.rows ?? []).map((r) => (
          <li key={r.activityId} className="py-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-medium [overflow-wrap:anywhere]">
                {r.code} {r.name}
              </span>
              {r.deviationPct != null ? (
                <Badge tone={r.deviationPct < -10 ? 'danger' : r.deviationPct < 0 ? 'warning' : 'success'}>
                  {pct(r.deviationPct)} к норме
                </Badge>
              ) : (
                <Badge>нет нормы</Badge>
              )}
            </div>
            <p className="text-[12px] text-ink-2">
              {n1(r.quantity)} {r.unit ?? ''} за {n1(r.machineHours)} маш.-ч
              {r.laborHours != null ? ` и ${n1(r.laborHours)} чел.-ч` : ''}
              {r.factPerHour != null ? ` · факт ${n1(r.factPerHour)}/ч` : ''}
              {r.normPerHour != null ? ` · норма ${n1(r.normPerHour)}/ч (${r.normName})` : ''}
              {r.forecastHoursLeft != null
                ? ` · осталось ${n1(r.remainingQuantity)} ${r.unit ?? ''} — ещё ${n1(r.forecastHoursLeft)} маш.-ч`
                : ''}
              {r.unitCost != null ? ` · ${money(r.unitCost)} за ${r.unit ?? 'ед.'}` : ''}
            </p>
          </li>
        ))}
      </ul>
    </Panel>
  );
}
